import React from 'react';
import { Alert, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AnalysisContent = styled(Box)(({ theme }) => ({
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  lineHeight: 1.7,
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    lineHeight: 1.35,
  },
  '& h1': { fontSize: '1.5rem' },
  '& h2': { fontSize: '1.3rem' },
  '& h3, & h4, & h5, & h6': { fontSize: '1.1rem' },
  '& p, & ul, & ol': { marginTop: 0, marginBottom: theme.spacing(2) },
  '& ul, & ol': { paddingLeft: theme.spacing(3) },
  '& li + li': { marginTop: theme.spacing(0.75) },
  '& strong': { fontWeight: 600 },
  '& a': { color: theme.palette.primary.main },
  '& blockquote': {
    margin: theme.spacing(2, 0),
    paddingLeft: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  '& pre': {
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  '& code': { fontSize: '0.875em', whiteSpace: 'break-spaces' },
  '& table': { borderCollapse: 'collapse', width: '100%', minWidth: 560, fontSize: '0.9rem' },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    verticalAlign: 'top',
    textAlign: 'left',
    minWidth: 110,
  },
  '& th': { backgroundColor: theme.palette.action.hover, fontWeight: 600 },
  '& img': { maxWidth: '100%', height: 'auto' },
  '& hr': { border: 0, borderTop: `1px solid ${theme.palette.divider}`, margin: theme.spacing(3, 0) },
}));

export function normalizeAnalysis(value) {
  if (typeof value !== 'string') return '';
  const text = value.replace(/\r\n?/g, '\n').trim();
  // Unwrap only one whole-document Markdown fence; preserve ordinary code examples.
  const fenced = text.match(/^(`{3,}|~{3,})(?:markdown|md)[ \t]*\n([\s\S]*?)\n\1[ \t]*$/i);
  if (fenced && !fenced[2].split('\n').some(line => line.trim() === fenced[1])) {
    return fenced[2].trim();
  }
  return text;
}

export function containsDraftAnalysis(text) {
  // These explicit self-editing phrases occurred in a saved report. Reject the
  // report as a whole instead of silently deleting sentences or inventing a repair.
  return /\b(?:wait cleaner set in final|will final markdown full below|please rewrite (?:the )?whole output|let me (?:actually )?self inspect|hold design message|let me (?:restart|rewrite|reorganize)|clean reformatted final answer|internal reasoning|my meta|final draft make)\b/i
    .test(text.replace(/[*_`]/g, ''));
}

function AnalysisTable({ node, ...props }) {
  return (
    <Box role="region" aria-label="Review analysis table" tabIndex={0}
      sx={{ maxWidth: '100%', overflowX: 'auto', mb: 2 }}>
      <table {...props} />
    </Box>
  );
}

export default function ReviewAnalysis({ analysis }) {
  const text = normalizeAnalysis(analysis);
  if (!text) {
    return <Alert severity="info">No AI analysis is available yet.</Alert>;
  }
  if (containsDraftAnalysis(text)) {
    return (
      <Alert severity="warning">
        This AI report is incomplete and cannot be displayed reliably.
        A new report will appear after a successful analysis update.
      </Alert>
    );
  }
  return (
    <AnalysisContent sx={{ mt: 3 }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ table: AnalysisTable }} skipHtml>
        {text}
      </ReactMarkdown>
    </AnalysisContent>
  );
}
